import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { UserPlus, CheckCircle2, XCircle, CalendarClock } from "lucide-react";

const empty = {
  name: "", phone: "", email: "", company: "", address: "", call_notes: "",
  outcome: "No Sale", plan_id: "", antivirus: "", license_key: "", amount: "",
  tech_assigned: "", work_notes: "",
};

const addDays = (d, days) => { const x = new Date(d); x.setDate(x.getDate() + days); return x.toISOString().slice(0, 10); };

export default function CustomerFormDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState(empty);
  const [plans, setPlans] = useState([]);
  const [antivirus, setAntivirus] = useState([]);
  const [techs, setTechs] = useState([]);
  const [saving, setSaving] = useState(false);
  const navigate = useNavigate();
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (!open) return;
    setForm(empty);
    api.get("/catalog/plans").then((r) => setPlans(r.data));
    api.get("/catalog/antivirus").then((r) => setAntivirus(r.data));
    api.get("/team/members").then((r) => setTechs((r.data || []).filter((m) => (m.role || "").toLowerCase() === "tech"))).catch(() => {});
  }, [open]);

  const plan = plans.find((p) => p.id === form.plan_id);
  const today = new Date().toISOString().slice(0, 10);
  const subEnd = plan ? addDays(today, plan.duration_days) : null;

  const choosePlan = (id) => {
    const p = plans.find((x) => x.id === id);
    setForm((f) => ({ ...f, plan_id: id, amount: p ? String(p.price) : f.amount }));
  };

  const submit = async () => {
    if (!form.name.trim()) { toast.error("Customer name is required"); return; }
    if (form.outcome === "Sale" && !form.plan_id) { toast.error("Select a plan for the sale"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/clients", { ...form, amount: parseFloat(form.amount) || 0 });
      toast.success(form.outcome === "Sale" ? `Sale recorded · case assigned to ${form.tech_assigned || "unassigned"}` : "Entry saved (No Sale)");
      onOpenChange(false);
      onSaved && onSaved();
      if (form.outcome === "Sale") navigate(`/clients/${data.id}`);
    } catch (e) {
      toast.error("Could not save the customer");
    } finally { setSaving(false); }
  };

  const isSale = form.outcome === "Sale";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl rounded-sm max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading"><UserPlus size={18} className="text-primary" /> New Customer</DialogTitle>
          <DialogDescription>Log the call. Choose the outcome — sale details appear when you mark it a Sale.</DialogDescription>
        </DialogHeader>

        {/* Contact */}
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label>Customer name *</Label>
            <Input data-testid="cust-name" value={form.name} onChange={(e) => set("name", e.target.value)} className="rounded-sm" />
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Phone</Label><Input data-testid="cust-phone" value={form.phone} onChange={(e) => set("phone", e.target.value)} className="rounded-sm font-mono" /></div>
            <div className="grid gap-1.5"><Label>Email</Label><Input data-testid="cust-email" value={form.email} onChange={(e) => set("email", e.target.value)} className="rounded-sm" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Company</Label><Input data-testid="cust-company" value={form.company} onChange={(e) => set("company", e.target.value)} className="rounded-sm" /></div>
            <div className="grid gap-1.5"><Label>Address</Label><Input data-testid="cust-address" value={form.address} onChange={(e) => set("address", e.target.value)} className="rounded-sm" /></div>
          </div>
          <div className="grid gap-1.5"><Label>Call notes</Label><Textarea data-testid="cust-call-notes" value={form.call_notes} onChange={(e) => set("call_notes", e.target.value)} rows={2} className="rounded-sm" /></div>
        </div>

        {/* Outcome */}
        <div className="grid grid-cols-2 gap-2 mt-1">
          <button onClick={() => set("outcome", "Sale")} data-testid="outcome-sale"
            className={`flex items-center justify-center gap-2 py-3 rounded-sm border text-sm font-medium transition-colors ${isSale ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}>
            <CheckCircle2 size={17} /> Sale
          </button>
          <button onClick={() => set("outcome", "No Sale")} data-testid="outcome-no-sale"
            className={`flex items-center justify-center gap-2 py-3 rounded-sm border text-sm font-medium transition-colors ${!isSale ? "border-primary bg-primary text-primary-foreground" : "border-border hover:bg-accent"}`}>
            <XCircle size={17} /> No Sale
          </button>
        </div>

        {/* Sale-only fields */}
        {isSale && (
          <div className="grid gap-3 mt-1 border-t border-border pt-4" data-testid="sale-fields">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="grid gap-1.5">
                <Label>Plan *</Label>
                <Select value={form.plan_id} onValueChange={choosePlan}>
                  <SelectTrigger className="rounded-sm" data-testid="cust-plan"><SelectValue placeholder="Select a plan" /></SelectTrigger>
                  <SelectContent>{plans.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — ${p.price}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid gap-1.5">
                <Label>Antivirus</Label>
                <Select value={form.antivirus} onValueChange={(v) => set("antivirus", v)}>
                  <SelectTrigger className="rounded-sm" data-testid="cust-antivirus"><SelectValue placeholder="Select antivirus" /></SelectTrigger>
                  <SelectContent>{antivirus.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="grid gap-1.5"><Label>Sale amount ($)</Label><Input data-testid="cust-amount" type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} className="rounded-sm font-mono" /></div>
              <div className="grid gap-1.5"><Label>License key</Label><Input data-testid="cust-license" value={form.license_key} onChange={(e) => set("license_key", e.target.value)} className="rounded-sm font-mono" /></div>
            </div>
            {plan && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground bg-secondary/60 rounded-sm px-3 py-2" data-testid="sub-preview">
                <CalendarClock size={15} className="text-primary" />
                Subscription: <span className="font-mono text-foreground">{today}</span> → <span className="font-mono text-foreground">{subEnd}</span> ({plan.duration_days} days)
              </div>
            )}
            <div className="grid gap-1.5">
              <Label>Assign to tech</Label>
              <Select value={form.tech_assigned} onValueChange={(v) => set("tech_assigned", v)}>
                <SelectTrigger className="rounded-sm" data-testid="cust-tech"><SelectValue placeholder="Select a tech" /></SelectTrigger>
                <SelectContent>{techs.map((t) => <SelectItem key={t.id} value={t.name}>{t.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Work to be done (for tech)</Label><Textarea data-testid="cust-work-notes" value={form.work_notes} onChange={(e) => set("work_notes", e.target.value)} rows={2} className="rounded-sm" placeholder="Installation & setup instructions for the assigned tech…" /></div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" className="rounded-sm" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button className="rounded-sm" onClick={submit} disabled={saving} data-testid="customer-submit">{saving ? "Saving…" : "Submit"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
